import React, { useMemo } from "react";
import Layout from "../Components/Layout";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,Stack,
  Alert,
  Button,
} from "@mui/material";
import {
  BarChart as BarChartIcon,
  Phone as PhoneIcon,
  Token,
  Timer,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Message,
  Call as CallIcon,
  Chat as ChatIcon,
  ChatBubble as ChatBubbleIcon,
  ChatBubbleRounded as ChatBubbleRoundedIcon,
  ChatSharp as ChatSharpIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { FONT_FAMILY } from "../Constant";
import { getWebhookData } from "../redux/actions/billingActions";
import useCallOnce from "../hooks/useCallOnce";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

function Dashboard() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { webhookData, loading, error } = useSelector((state) => state.billing);
  // Fetch webhook data using useCallOnce - only once
  useCallOnce("billing/getWebhookData", () => {
    dispatch(getWebhookData());
  });

  // console.log(useSelector((state) => state.auth));

  // Function to get formatted date
  const getFormattedDate = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Function to get greeting based on current time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return "Good Morning";
    } else if (hour >= 12 && hour < 17) {
      return "Good Afternoon";
    } else if (hour >= 17 && hour < 21) {
      return "Good Evening";
    } else {
      return "Good Evening";
    }
  };

  // Calculate webhook data summary
  const getWebhookSummary = () => {
    if (!webhookData || !webhookData.data || !Array.isArray(webhookData.data)) {
      return {
        totalWebhooks: 0,
        totalCost: 0,
        totalDuration: 0,
        percredit: 0,
        agentZenaPercentage: 0,
      };
    }

    const webhookArray = webhookData.data;

    const summary = {
      totalWebhooks: webhookArray.length,
      totalCost: 0,
      totalDuration: 0,
      percredit: webhookData?.getCosting[0]?.percredit || 0,
      agentZenaPercentage: webhookData?.getCosting[0]?.agentzenapercent || 0,
    };

    webhookArray.forEach((webhook) => {
      // Cost calculation
      if (webhook.price) {
        summary.totalCost += webhook.price;
      }

      // Duration calculation
      if (webhook.body?.data?.metadata?.call_duration_secs) {
        summary.totalDuration += webhook.body.data.metadata.call_duration_secs;
      }
    });

    return summary;
  };

  // Prepare chart data for last 30 days
  const chartData = useMemo(() => {
    if (!webhookData || !webhookData.data || !Array.isArray(webhookData.data)) {
      return [];
    }

    const webhookArray = webhookData.data;
    const dataMap = new Map();

    webhookArray.forEach((webhook) => {
      if (webhook.price) {
        const date = new Date(webhook.created_at);
        const key = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        if (dataMap.has(key)) {
          dataMap.set(key, dataMap.get(key) + webhook.price);
        } else {
          dataMap.set(key, webhook.price);
        }
      }
    });

    // Generate last 30 days with data or 0
    const currentDate = new Date();
    const last30Days = [];

    // Generate array of last 30 days
    for (let i = 29; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - i);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      const dateKey = `${year}-${month}-${day}`;
      const displayDate = date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
      });

      last30Days.push({
        date: displayDate,
        originalDate: dateKey,
        price: dataMap.has(dateKey)
          ? Math.round(dataMap.get(dateKey) * 100) / 100
          : 0,
      });
    }

    return last30Days;
  }, [webhookData]);

  const webhookSummary = getWebhookSummary();

  // Calculate total cost in dollars (same as BillingUsagePlans)
  // const totalCreditsCost = webhookSummary.totalCost * webhookSummary.percredit;
  // const totalZenaCost = (webhookSummary.agentZenaPercentage / 100) * totalCreditsCost;
  // const totalAmount = totalCreditsCost + totalZenaCost;

  // Format duration to rounded minutes with 'min' suffix
  const formatDuration = (seconds) => {
    if (!seconds) return "0 min";
    const minutes = Math.round(seconds / 60);
    return `${minutes} min`;
  };

  // Metric cards data with real data from webhook
  // const metrics = [
  //   {
  //     label: 'Conversations',
  //     value: webhookSummary.totalWebhooks.toLocaleString(),
  //     icon: PhoneIcon,
  //     color: '#10b981'
  //   },
  //   {
  //     label: 'Total Credits',
  //     value: webhookSummary.totalCost.toLocaleString(),
  //     icon: Token,
  //     color: '#f59e0b'
  //   },
  //   {
  //     label: 'Total Duration',
  //     value: formatDuration(webhookSummary.totalDuration),
  //     icon: Timer,
  //     color: '#8b5cf6'
  //   },
  //   {
  //     label: 'Total Cost',
  //     value: `$${totalAmount.toFixed(4)}`,
  //     icon: MoneyIcon,
  //     color: '#ef4444'
  //   }
  // ];

  const metrics = [
    {
      label: "Messages",
      value: (webhookData?.processedData?.results[0]?.total_messages || 0).toLocaleString(),
      // perValue: `$${(webhookData?.processedData?.perMessage || 0).toFixed(4)}`,
      // perValue: `$${(
      //   ((webhookData?.processedData?.totalCharges_llm ?? 0) +
      //     (webhookData?.getCosting?.[0]?.percredit ?? 0) *
      //       (webhookData?.processedData?.results[0]?.total_call_credits ?? 0)) /
      //   webhookData?.processedData?.results[0]?.total_messages ?? 0
      // ).toFixed(4)}`,
      // perValue: `$${((
      //   (webhookData?.getCosting?.[0]?.percredit || 0) *
      //   (webhookData?.processedData?.results[0]?.total_call_credits || 0)
      // ) / (webhookData?.processedData?.results[0]?.total_messages || 1)).toFixed(4)}`,
      // perLabel: "Average cost per message",
      icon: Message,
      color: "#10b981",
    },
    {
      label: "Minutes",
      value: formatDuration(
        webhookData?.processedData?.results[0]?.total_call_duration_secs
      ),
      // perValue: `$${(webhookData?.processedData?.perMinuteDollars || 0).toFixed(
      //   4
      // )}`,
      // perValue: `$${(
      //   ((webhookData?.processedData?.totalCharges_llm ?? 0) +
      //     (webhookData?.getCosting?.[0]?.percredit ?? 0) *
      //       (webhookData?.processedData?.results[0]?.total_call_credits ?? 0)) /
      //   ((webhookData?.processedData?.results[0]?.total_call_duration_secs ??
      //     1) /
      //     60)
      // ) // convert secs → mins
      //   .toFixed(4)}`,
      // perValue: `$${((webhookData?.processedData?.totalCharges_llm || 0) / ((webhookData?.processedData?.results[0]?.total_call_duration_secs || 0) / 60 || 1)).toFixed(3)}`,
      // perLabel: "Average cost per minute",
      icon: Timer,
      color: "#10b981",
    },
    {
      label: "Calls",
      value: (webhookData?.processedData?.results[0]?.total_conversation || 0).toLocaleString(),
      // perValue: `$${(webhookData?.processedData?.results[0]?.total_conversation / webhookData?.processedData?.totalCharges_llm + (webhookData?.getCosting?.[0]?.percredit * webhookData?.processedData?.results[0]?.total_call_credits) ).toFixed(4)}`,
      // perValue: `$${(
      //   ((webhookData?.processedData?.totalCharges_llm ?? 0) +
      //     (webhookData?.getCosting?.[0]?.percredit ?? 0) *
      //       (webhookData?.processedData?.results[0]?.total_call_credits ?? 0)) /
      //   (webhookData?.processedData?.results[0]?.total_conversation || 1)
      // ).toFixed(4)}`,
      // perLabel: "Average cost per call",
      icon: PhoneIcon,
      color: "#10b981",
    },
    // {
    //   label: "Chat Credits",
    //   value: (webhookData?.processedData?.results[0]?.total_call_credits || 0).toLocaleString(),
    //   // perValue: `$${(webhookData?.processedData?.perMinuteDollars || 0).toFixed(4)}`,
    //   perValue: null,
    //   perLabel: null,
    //   icon: ChatSharpIcon,
    //   color: "#555",
    // },
    // {
    //   label: "LLM Credit",
    //   value: (webhookData?.processedData?.results[0]?.lmmCredits || 0).toLocaleString(),
    //   perValue: null,
    //   perLabel: null,
    //   icon: Token,
    //   color: "#555",
    // },
    // {
    //   label: "Total Credits",
    //   value: (
    //     (webhookData?.processedData?.results[0]?.lmmCredits || 0) +
    //     (webhookData?.processedData?.results[0]?.total_call_credits || 0)
    //   ).toLocaleString(),
    //   perValue: Math.round(((
    //     (webhookData?.processedData?.results[0]?.lmmCredits || 0) +
    //     (webhookData?.processedData?.results[0]?.total_call_credits || 0)
    //   ) / (webhookData?.processedData?.results[0]?.total_conversation || 1)),
    //   4) + " Credits/Call",
    //   perLabel: "Average Cost",
    //   icon: Token,
    //   color: "#10b981",
    // },
    // {
    //   label: "Chat Cost",
    //   value: `$${(
    //     (webhookData?.getCosting?.[0]?.percredit || 0) *
    //     (webhookData?.processedData?.results[0]?.total_call_credits || 0)
    //   ).toFixed(2)}`,
    //   perValue: null,
    //   perLabel: null,
    //   icon: MoneyIcon,
    //   color: "#ef4444",
    // },
    // {
    //   label: "LLM Cost",
    //   value: `$${(webhookData?.processedData?.totalCharges_llm || 0).toFixed(
    //     2
    //   )}`,
    //   perValue: null,
    //   perLabel: null,
    //   icon: MoneyIcon,
    //   color: "#ef4444",
    // },
    // {
    //   label: "Total Cost",
    //   value: `$${(
    //     (webhookData?.processedData?.totalCharges_llm || 0) +
    //     (webhookData?.getCosting?.[0]?.percredit || 0) *
    //       (webhookData?.processedData?.results[0]?.total_call_credits || 0)
    //   ).toFixed(2)}`,
    //   perValue: null,
    //   perLabel: null,
    //   icon: MoneyIcon,
    //   color: "#ef4444",
    // },
  ];

  return (
    <Layout>
      <Box sx={{ mt: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2 } }}>

        {/* Header Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 }, px: { xs: 1, sm: 0 } }}>
          <Typography
            variant="body2"
            sx={{
              color: "#9ca3af",
              fontSize: { xs: "12px", sm: "14px" },
              mb: { xs: 0.25, sm: 0.5 },
              fontFamily: FONT_FAMILY,
            }}
          >
            My Workspace
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#111827",
              fontWeight: 600,
              fontSize: { xs: "12px", sm: "14px", md: "16px" },
              mt: { xs: 0.25, sm: 0.5, md: 1 },
              fontFamily: FONT_FAMILY,
            }}
          >
            {getFormattedDate()}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#111827",
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.25rem" },
              fontFamily: FONT_FAMILY,
            }}
          >
            {getGreeting()}, {user?.company || "User"}
          </Typography>
        </Box>

        {/* Metric Cards Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 }, px: { xs: 1, sm: 0 } }}>
          <Box
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f3f4f6",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d1d5db",
                borderRadius: "3px",
              },
            }}
          >
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {metrics.map((metric, index) => {
                const IconComponent = metric.icon;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                    <Card
                      sx={{
                        height: "100%",
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        boxShadow: "none",
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          borderColor: "#d1d5db",
                          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <CardContent
                        sx={{ p: { xs: 1.5, sm: 2 }, position: "relative" }}
                      >
                        {/* Icon */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: { xs: 24, sm: 28 },
                              color: metric.color,
                            }}
                          />
                        </Box>

                        {/* Metric Label */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b7280",
                            fontSize: { xs: "11px", sm: "12px", md: "13px" },
                            mb: { xs: 0.75, sm: 1 },
                            fontWeight: 500,
                            fontFamily: FONT_FAMILY,
                            textAlign: "center",
                          }}
                        >
                          {metric.label}
                        </Typography>

                        {/* Metric Value */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {loading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: {
                                  xs: "1.5rem",
                                  sm: "1.75rem",
                                  md: "2.25rem",
                                },
                              }}
                            >
                              <CircularProgress
                                size={20}
                                sx={{ color: metric.color }}
                              />
                            </Box>
                          ) : (
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: "#111827",
                                fontSize: {
                                  xs: "1.1rem",
                                  sm: "1.25rem",
                                  md: "1.5rem",
                                },
                                fontFamily: FONT_FAMILY,
                              }}
                            >
                              {metric.value}
                            </Typography>
                          )}

                          {/* Per Value */}
                          {metric.perValue && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.25,
                              }}
                            >
                              {loading ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: "1rem",
                                  }}
                                >
                                  <CircularProgress
                                    size={12}
                                    sx={{ color: metric.color }}
                                  />
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: metric.color,
                                    fontSize: {
                                      xs: "10px",
                                      sm: "11px",
                                      md: "12px",
                                    },
                                    fontFamily: FONT_FAMILY,
                                  }}
                                >
                                  {metric.perValue}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#9ca3af",
                                  fontSize: { xs: "9px", sm: "10px" },
                                  fontFamily: FONT_FAMILY,
                                  textAlign: "center",
                                }}
                              >
                                {metric.perLabel}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>

        {/* Main Content Area */}
        {/* <Card 
          sx={{ 
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            minHeight: { xs: 300, sm: 400 },
            mx: { xs: 1, sm: 0 }
          }}
        >
          <CardContent sx={{ 
            py: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 4 }
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: { xs: 6, sm: 8 }
              }}>
                <CircularProgress size={40} sx={{ color: '#111827' }} />
              </Box>
            ) : error ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: { xs: 6, sm: 8 }
              }}>
                <BarChartIcon sx={{ 
                  fontSize: { xs: 48, sm: 56, md: 64 }, 
                  color: '#ef4444', 
                  mb: { xs: 1.5, sm: 2 } 
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: '#ef4444',
                    mb: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  Error loading data
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#9ca3af',
                    textAlign: 'center',
                    maxWidth: { xs: 300, sm: 400 },
                    fontSize: { xs: '12px', sm: '14px' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  {error}
                </Typography>
              </Box>
            ) : webhookSummary.totalWebhooks > 0 ? (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ color: '#10b981', fontSize: 24 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        fontFamily: FONT_FAMILY
                      }}
                    >
                                             Last 30 Days Analysis
                    </Typography>
                  </Box>
                  
                  
                </Box>

                {chartData.length > 0 ? (
                  <Box sx={{ height: { xs: 250, sm: 300, md: 350 } }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                         <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                         <XAxis 
                           dataKey="date" 
                           stroke="#6b7280"
                           fontSize={10}
                           tick={{ fill: '#6b7280' }}
                           angle={-45}
                           textAnchor="end"
                           height={70}
                           interval={0}
                         />
                                                 <YAxis 
                           stroke="#6b7280"
                           fontSize={12}
                           tick={{ fill: '#6b7280' }}
                           tickFormatter={(value) => `${value}`}
                         />
                         <Tooltip 
                           contentStyle={{
                             backgroundColor: '#ffffff',
                             border: '1px solid #e5e7eb',
                             borderRadius: '8px',
                             boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                           }}
                           labelStyle={{ color: '#111827', fontWeight: 600 }}
                           formatter={(value, name) => [`${value}`, 'Credit']}
                         />
                        <Line 
                          type="monotone" 
                          dataKey="price" 
                          stroke="#10b981" 
                          strokeWidth={3}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: { xs: 4, sm: 6 }
                  }}>
                    <BarChartIcon sx={{ 
                      fontSize: { xs: 48, sm: 56 }, 
                      color: '#9ca3af', 
                      mb: { xs: 1.5, sm: 2 } 
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#6b7280',
                        textAlign: 'center',
                        fontSize: { xs: '14px', sm: '16px' },
                        fontFamily: FONT_FAMILY
                      }}
                    >
                      No cost data available for the selected period
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: { xs: 6, sm: 8 }
              }}>
                <BarChartIcon sx={{ 
                  fontSize: { xs: 48, sm: 56, md: 64 }, 
                  color: '#9ca3af', 
                  mb: { xs: 1.5, sm: 2 } 
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: '#6b7280',
                    mb: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  No metrics
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#9ca3af',
                    textAlign: 'center',
                    maxWidth: { xs: 300, sm: 400 },
                    fontSize: { xs: '12px', sm: '14px' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  There are no metrics for the specified period.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card> */}
      </Box>
    </Layout>
  );
}

export default Dashboard;
